import {useState} from 'react';

import {
  getIsParkingFeatureVisible,
  getMeetingsByDate,
  getMeetingsByWorkday,
  useAppSelector,
  useOverridableFeatureFlags,
} from '@lib/store';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {Workday} from '@lib/store/src/data/timeslots/apiTypes';
import {getWorkdaysForDate} from '@lib/store/src/data/timeslots/selectors';
import {isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';
import {WorkdayConnectionsSection, WorkdayMeetingsSection, WorkdayParkingSection} from './WorkdaySections';
import {WorkdayReservationsSection} from './WorkdaySections/WorkdayReservationsSection';
import {CalendarEventCell} from '../CalendarEventCell';

interface WorkdayContentProps {
  workday: Workday | undefined;
  selectedDate: string;
}

export const WorkdayContent = ({workday, selectedDate}: WorkdayContentProps) => {
  const features = useOverridableFeatureFlags();
  const date = useMemoDate(selectedDate);
  const workdays = useAppSelector((state) => getWorkdaysForDate(state, selectedDate));
  const isParkingFeatureVisible = useAppSelector(getIsParkingFeatureVisible);

  const [isMeetingListVisible, setIsMeetingListVisible] = useState(workdays?.data ? workdays.data.length <= 1 : false);

  const meetings = useAppSelector((state) =>
    workday ? getMeetingsByWorkday(state, workday) : getMeetingsByDate(state, date),
  );

  return (
    <>
      <div className="mt-2 bg-white rounded-xl empty:mt-0">
        {/* Connections */}
        {isOfficeWorkday(workday) && (
          <WorkdayConnectionsSection
            workday={workday}
            date={date}
          />
        )}

        {/* Parking */}
        {isParkingFeatureVisible && (
          <WorkdayParkingSection
            workday={workday}
            date={date}
          />
        )}

        {/* Workspace reservations */}
        <WorkdayReservationsSection
          workday={workday}
          date={date}
        />

        {/* Meetings */}
        {features.HybridMeetingsCalendarView && (
          <WorkdayMeetingsSection
            workday={workday}
            selectedDate={selectedDate}
            initialOpen={isMeetingListVisible}
            onPress={() => setIsMeetingListVisible((prev) => !prev)}
          />
        )}
      </div>

      {/* Meetings sit visually outside the WorkdayContent container */}
      {features.HybridMeetingsCalendarView && isMeetingListVisible && !!meetings.length ? (
        <>
          {meetings.length ? (
            <div className="mt-4 flex flex-col gap-4">
              {meetings.map((meeting) => (
                <CalendarEventCell
                  key={meeting.id}
                  meeting={meeting}
                  date={selectedDate}
                />
              ))}
            </div>
          ) : (
            <></>
            // TODO: MeetingEmptyListContent
          )}
        </>
      ) : null}
    </>
  );
};
